import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const OilPaintingVideosPage = () => {
    return (
        <div data-testid="oil-painting-videos-page">
            <SEO
                title="Oil Painting Videos & Workshops"
                description="Watch professional oil painting videos and workshops from master artists. Alla prima demonstrations, portrait painting, landscape painting, and classical techniques by Tony Pro, Jeremy Lipking, and more."
                url="/oil-painting-videos"
                keywords={['oil painting videos', 'oil painting workshops', 'alla prima painting video', 'oil painting tutorial', 'oil painting demonstration']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'WebPage',
                    '@id': 'https://zarolla.com/oil-painting-videos#webpage',
                    url: 'https://zarolla.com/oil-painting-videos',
                    name: 'Oil Painting Videos & Workshops | Zarolla Online Art Academy',
                    description: 'Watch professional oil painting videos and workshops from master artists. Alla prima demonstrations, portrait and landscape painting.',
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Watch masters at work
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Oil painting videos for serious artists.
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            There is no substitute for watching a master painter work. Zarolla's oil painting videos capture accomplished artists in action — mixing color, massing values, refining edges, and making the hundreds of decisions that produce a finished painting. Every brushstroke is visible. Every choice is explained.
                        </p>
                    </div>

                    {/* Section 1 */}
                    <section className="py-12 border-t border-border">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    Real painting, start to finish
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Our workshop videos are complete painting demonstrations — not time-lapses, not edited highlight reels. You see the full process from blank canvas to finished painting, including the moments of problem-solving and adjustment that are usually hidden from students.
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Artists like Tony Pro, Jeremy Lipking, Clayton J. Beck III, and Dan Schultz work through portraits, figures, landscapes, and still life subjects, explaining their thinking as they paint. You'll see how professionals handle the palette, manage edges, control values, and maintain the painting's overall design throughout the session.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    These are the same demonstrations that students pay hundreds of dollars to attend in person at ateliers and workshops. On Zarolla, you can watch them as many times as you need, pausing and rewinding to study each decision.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">Subjects and techniques covered:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Alla prima oil painting — wet-on-wet from start to finish</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Portrait painting in oil</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Figure painting and the clothed model</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Landscape and plein air painting</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Color mixing and palette management</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Value control and edge quality</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="py-12 border-t border-border">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            Two ways to study oil painting on Zarolla
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-3">Painting workshops</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                                    Single-session demonstrations where a professional artist completes a painting while explaining their process. These are ideal for seeing how finished paintings come together and for absorbing the rhythm of professional work.
                                </p>
                                <Link to="/workshops" className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1">
                                    Browse painting videos <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-3">Multi-session courses</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                                    Structured learning paths that build your painting skills methodically over multiple lessons. Courses cover technique, theory, and application in depth — the closest thing to an atelier education you can get online.
                                </p>
                                <Link to="/courses" className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1">
                                    Browse courses <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: CTA */}
                    <section className="py-12 border-t border-border">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                See how the masters paint
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Start with a workshop video that interests you — a portrait session, a landscape, or an alla prima demonstration — and see firsthand what professional instruction looks like on Zarolla.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/workshops">
                                        Browse Painting Videos <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/courses">View Courses</Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/about">Meet the Artists</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default OilPaintingVideosPage;
